import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How often to re-check while the tab sits focused. Focus alone misses the tab
 * nobody switches away from — an accountant with the portal open all day.
 */
const POLL_INTERVAL_MS = 15 * 60 * 1000;

/** Collapses the burst of focus events a window switch can produce. */
const FOCUS_DEBOUNCE_MS = 2000;

const VERSION_URL = "/version.json";

/**
 * Reads the deployed build id, or null if we cannot get a trustworthy answer.
 *
 * Every failure returns null rather than throwing. `vercel.json` rewrites
 * unmatched paths to index.html, so a missing version.json comes back as an
 * HTML body with status 200 — which is why the content-type is checked before
 * the body is parsed, and the parse is guarded on top of that.
 */
async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return null;
    if (!res.headers.get("content-type")?.includes("application/json")) {
      return null;
    }
    const data: unknown = JSON.parse(await res.text());
    if (typeof data !== "object" || data === null) return null;
    const { buildId } = data as { buildId?: unknown };
    return typeof buildId === "string" && buildId ? buildId : null;
  } catch {
    return null;
  }
}

/**
 * True once the deployed build differs from the one this tab is running.
 *
 * A Vite SPA runs whatever bundle it downloaded at page load, forever — there
 * is no service worker and nothing else re-checks. Without this, a tab left
 * open across a deploy keeps executing old code and produces plausible but
 * wrong behavior, which reads as a bug in the shipped fix rather than as
 * staleness.
 *
 * Latches on: once stale, the tab stays stale until it reloads, so a later
 * failed check can never retract the banner.
 */
export function useBuildVersionCheck(): boolean {
  const [isStale, setIsStale] = useState(false);
  // Read through a ref so the effect below doesn't re-subscribe on every
  // state change, and so the check can bail without depending on isStale.
  const isStaleRef = useRef(false);

  const check = useCallback(async (): Promise<void> => {
    if (isStaleRef.current) return;
    const deployed = await fetchDeployedBuildId();
    if (deployed === null || deployed === __BUILD_ID__) return;
    isStaleRef.current = true;
    setIsStale(true);
  }, []);

  useEffect(() => {
    // version.json is emitted by the build, so it does not exist under `vite
    // dev` — the dev server would answer with index.html and the guard would
    // reject it every time. Skip entirely rather than poll for nothing.
    if (import.meta.env.DEV) return;

    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    const onFocus = (): void => {
      clearTimeout(focusTimer);
      focusTimer = setTimeout(() => void check(), FOCUS_DEBOUNCE_MS);
    };

    void check();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS);

    return () => {
      clearTimeout(focusTimer);
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  return isStale;
}
